import FolderIcon from '@components/Projects/FolderIcon';
import { type CustomFolderChildRow, folderRowsOf } from '@components/Projects/folderRows';
import { compareItems, type FolderSortKey } from '@components/Projects/folderSort';
import { useLocalization } from '@lib/localization';
import { buildFolderPath } from '@lib/routing/projectPath';
import { Box, IconButton, ListItemButton, Skeleton, Typography } from '@mui/material';
import { treeChevronStyles, treeItemStyles, treeListStyles, treeRowStyles } from '@pages/Projects.styles';
import { useGetFolderByPathQuery } from '@store/api/folderEndpoints';
import { ChevronRightIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

/** Opens a folder's menu, the one the listing has, with the path of the folder it is in. */
type OpenMenu = (event: React.MouseEvent<HTMLElement>, row: CustomFolderChildRow, parentPath: string) => void;

interface FolderTreePaneProps {
  /** The path of the folder in view, or none on search results, where no folder is. */
  currentPath?: string;
  /** The item whose menu is open, drawn as the listing draws its own. */
  activeId?: string;
  /** The listing's order, which the folders of each level follow. */
  sort: FolderSortKey;
  onMenu: OpenMenu;
}

interface LevelProps extends FolderTreePaneProps {
  /** The folder whose folders this level shows. */
  path: string;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
}

// The folders other teams shared first, then the teams' own folders, then the rest, as the listing has them.
const rankOf = (row: CustomFolderChildRow) => (row.isSharedWithMe ? 0 : row.isTeamRoot ? 1 : 2);

// Every folder above `path`, and `path` itself, the root left out: `/A/B` is `/A` and `/A/B`.
const trailOf = (path: string) => {
  const parts = path.split('/').filter(Boolean);
  return parts.map((_, index) => `/${parts.slice(0, index + 1).join('/')}`);
};

// A folder's row indents under its parent's; the root's own items start at the pane's edge.
const indentOf = (depth: number) => ({ paddingLeft: depth * 14 });

/**
 * The folders of one folder, read as the listing reads a folder (`getFolderByPath`) and from the
 * same cache: a level shows the folders the listing would show there, permissions included, and is
 * read again whenever a change made here invalidates that folder. Its projects stay in the listing.
 */
const TreeLevel = ({ path, depth, currentPath, activeId, sort, expanded, onToggle, onMenu }: LevelProps) => {
  const { currentLanguage } = useLocalization();
  const { currentData: folder, isLoading } = useGetFolderByPathQuery(path);

  if (isLoading) return <Skeleton variant="text" width={120} sx={{ ml: `${(depth + 1) * 14 + 24}px` }} />;

  const folders = folderRowsOf(folder)
    .sort((a, b) => rankOf(a) - rankOf(b) || compareItems(sort, currentLanguage, a, b));

  if (folders.length === 0) return null;

  return (
    <Box component="ul" sx={treeListStyles}>
      {folders.map((row) => {
        if (row.type !== 'Folder') return null;

        const rowPath = row.item.path;
        const isOpen = expanded.has(rowPath);
        const isCurrent = rowPath === currentPath;
        // A folder's children are listed by id with their kind, so whether it holds a folder is
        // known before it is read; one that holds only projects has nothing to open on here.
        const hasFolders = row.item.children.some((child) => child.type === 'Folder');

        return (
          <Box key={row.id} component="li" sx={treeItemStyles}>
            <Box sx={treeRowStyles} style={indentOf(depth)}>
              {hasFolders
                ? <IconButton
                  size="small"
                  aria-label={row.name}
                  aria-expanded={isOpen}
                  sx={treeChevronStyles}
                  onClick={() => onToggle(rowPath)}
                >
                  <ChevronRightIcon size={14} style={{ transform: isOpen ? 'rotate(90deg)' : undefined }} />
                </IconButton>
                : <Box sx={treeChevronStyles} />}
              {/* A link, so it opens in a new tab as any other does. */}
              <ListItemButton
                component={RouterLink}
                to={buildFolderPath(rowPath)}
                selected={isCurrent}
                aria-current={isCurrent ? 'page' : undefined}
                className={row.id === activeId ? 'is-active' : undefined}
                onContextMenu={(event: React.MouseEvent<HTMLElement>) => onMenu(event, row, path)}
              >
                <FolderIcon size={18} emblem={row.isTeamRoot ? 'team' : row.isSharedWithMe ? 'shared' : undefined} />
                <Typography noWrap>{row.name}</Typography>
              </ListItemButton>
            </Box>
            {isOpen &&
              <TreeLevel
                path={rowPath}
                depth={depth + 1}
                currentPath={currentPath}
                activeId={activeId}
                sort={sort}
                expanded={expanded}
                onToggle={onToggle}
                onMenu={onMenu} />}
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * The folders the reader can see, as a tree to go through them by. Each folder is read only once it
 * is opened, so the tree costs what the reader looks at, not every folder there is. The folder in
 * view is marked, and every folder above it is opened.
 */
const FolderTreePane = ({ currentPath, activeId, sort, onMenu }: FolderTreePaneProps) => {
  const { t } = useLocalization();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Only when the folder in view changes, so a folder the reader closed stays closed while they stay.
  useEffect(() => {
    if (!currentPath) return;
    setExpanded((open) => new Set([...open, ...trailOf(currentPath)]));
  }, [currentPath]);

  const toggle = (path: string) => setExpanded((open) => {
    const next = new Set(open);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    return next;
  });

  const isRootCurrent = currentPath === '/';

  return (
    <Box component="nav" aria-label={t('Common_Folders')}>
      <Box component="ul" sx={treeListStyles}>
        <Box component="li" sx={treeItemStyles}>
          {/* The root is the page itself and no folder: it is always open, and has no menu. */}
          <Box sx={treeRowStyles}>
            <ListItemButton
              component={RouterLink}
              to={buildFolderPath('/')}
              selected={isRootCurrent}
              aria-current={isRootCurrent ? 'page' : undefined}
            >
              <FolderIcon size={18} />
              <Typography noWrap>{t('Common_Folders')}</Typography>
            </ListItemButton>
          </Box>
          <TreeLevel
            path="/"
            depth={0}
            currentPath={currentPath}
            activeId={activeId}
            sort={sort}
            expanded={expanded}
            onToggle={toggle}
            onMenu={onMenu} />
        </Box>
      </Box>
    </Box>
  );
};

export default FolderTreePane;
